import React from 'react';
import { useIntl } from 'react-intl';
import { ArticleMode } from './ArticleTabs';
import { SelectOption } from '../../../../types/selectOption.type';
import ArtSelect, { ArtSelectOption } from './ArtSelect';

interface Props {
    mode: ArticleMode;
    search: string;
    onSearchChange: (value: string) => void;
    departmentOptions: SelectOption[];
    selectedDepartmentId: number | null;
    onDepartmentChange: (id: number | null) => void;
    supplierOptions: SelectOption[];
    selectedSupplierId: number | null;
    onSupplierChange: (id: number | null) => void;
}

const toArtOptions = (list: SelectOption[]): ArtSelectOption[] =>
    list.map((o) => ({ value: o.value as number | string, label: String(o.label ?? '') }));

const ArticleFilters: React.FC<Props> = ({
    mode,
    search,
    onSearchChange,
    departmentOptions,
    selectedDepartmentId,
    onDepartmentChange,
    supplierOptions,
    selectedSupplierId,
    onSupplierChange,
}) => {
    const intl = useIntl();

    const searchPlaceholder = intl.formatMessage({
        id: 'admin.articles.searchPlaceholder',
        defaultMessage: 'Kërko sipas emrit ose kodit...',
    });

    const allDepartmentsLabel = intl.formatMessage({
        id: 'admin.articles.filterAllDepartments',
        defaultMessage: 'Të gjitha repartet',
    });

    const allSuppliersLabel = intl.formatMessage({
        id: 'admin.articles.filterAllSuppliers',
        defaultMessage: 'Të gjithë furnitorët',
    });

    const showDepartment = mode === 'all' || mode === 'production';
    const showSupplier = mode === 'all' || mode === 'purchased';

    return (
        <div className="art-filters">
            <div className="art-search">
                <svg className="art-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    aria-label={searchPlaceholder}
                />
                {search && (
                    <button type="button" className="art-search__clear" aria-label="clear" onClick={() => onSearchChange('')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}
            </div>

            {showDepartment && (
                <ArtSelect
                    className="art-filters__select"
                    value={selectedDepartmentId}
                    onChange={(v) => onDepartmentChange(v === null ? null : Number(v))}
                    options={toArtOptions(departmentOptions)}
                    placeholder={allDepartmentsLabel}
                    allowClear
                    ariaLabel={allDepartmentsLabel}
                />
            )}

            {showSupplier && (
                <ArtSelect
                    className="art-filters__select"
                    value={selectedSupplierId}
                    onChange={(v) => onSupplierChange(v === null ? null : Number(v))}
                    options={toArtOptions(supplierOptions)}
                    placeholder={allSuppliersLabel}
                    allowClear
                    ariaLabel={allSuppliersLabel}
                />
            )}
        </div>
    );
};

export default ArticleFilters;
